import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { JhiEventManager } from '../../../shared/core-utils';
import { OntologyContext } from '../../ontology.context';
import { PopupService } from '../../../shared/modal/popup.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { ModeView } from '../../../shared/ontology/model/mode-view.enum';
import { PropertyDetails } from '../../../shared/ontology/model/property-details';
import { ActivatedRoute, Router } from '@angular/router';
import { OntologyService } from '../../../shared/ontology/service/ontology.service';
import { finalize } from 'rxjs/internal/operators/finalize';
import { PropertyService } from '../../../shared/ontology/service/property.service';
import { DropdownOptionData } from '../../../shared/column-filter/column-filter-dropdown-option-data';
import { ModalConfirmComponent } from '../../../shared/modal/modal-confirm.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'jhi-property-dialog',
    templateUrl: './property-dialog.component.html',
    styleUrls: ['./../../ontology-manager.scss']
})
export class PropertyDialogComponent implements OnInit, OnDestroy {
    protected readonly NAME_TEXT_LIMIT = 200;
    protected readonly DESCRIPTION_TEXT_LIMIT = 1024;
    protected readonly CATEGORY_VALUE_TEXT_LIMIT = 255;
    protected readonly MIN_RANGE = -999999999;
    protected readonly MAX_RANGE = 999999999;
    protected readonly SPECIAL_CHARACTERS = '^[^<>?|]*$';

    protected readonly ModeView = ModeView;
    protected readonly NUM_EDITABLE_FIELDS = 4;

    classes: DropdownOptionData[];
    selectedClasses: DropdownOptionData[];
    isLoading: boolean;
    showRangeWidget: boolean;
    showCategoricalWidget: boolean;
    modeView: ModeView;
    propertyDetails!: PropertyDetails;
    datatypeSelected: number;
    propertyId: string | null = null;
    variableId: string | null = null;

    constructor(public activeModal: NgbActiveModal,
                private modalService: NgbModal,
                private router: Router,
                private eventManager: JhiEventManager,
                private alertService: AlertService,
                private ontologyService: OntologyService,
                private propertyService: PropertyService,
                private ontologyContext: OntologyContext,
                private translateService: TranslateService) {
        this.propertyDetails = new PropertyDetails();
    }

    ngOnInit(): void {
        this.isLoading = true;
        this.datatypeSelected = null;
        this.selectedClasses = [];
        this.modeView = this.propertyId ? ModeView.VIEW : ModeView.CREATE;
        if (this.modeView === ModeView.VIEW) {
            this.propertyService.getPropertyId(this.propertyId).toPromise().then(propertyDetails => {
                this.ontologyContext.propertyDetails = propertyDetails;
                this.propertyDetails = structuredClone(this.ontologyContext.propertyDetails);
                this.selectedClasses = this.propertyDetails.classes.map((value: string) => {
                    return {
                        id: value,
                        text: value
                    };
                });
            });
        }
        this.ontologyService.getClasses().toPromise().then((result) => {
            this.classes = result.map((value: string) => {
                return {
                    id: value,
                    text: value
                };
            });
            this.isLoading = false;
        });
    }

    edit() {
        this.modeView = ModeView.EDIT;
    }

    discard() {
        if (this.modeView === ModeView.CREATE) {
            this.clear();
        } else {
            this.modeView = ModeView.VIEW;
            this.propertyDetails = structuredClone(this.ontologyContext.propertyDetails);
        }
    }

    isDeletable() {
        return this.propertyDetails?.metadata?.deletable;
    }

    ngOnDestroy(): void {
        this.ontologyContext.propertyDetails = null;
    }

    clear() {
        this.navigateToVariableView()
    }

    save() {
        this.isLoading = true;
        if (this.modeView === ModeView.EDIT) {
            this.propertyService.update(this.propertyDetails).pipe(
                finalize(() => this.isLoading = false)
            ).subscribe(() => {
                this.alertService.success('ontology-manager.tab.properties.modal.property.edited.success');
                this.notifyChanges();
            });
        } else {
            this.propertyService.create(this.propertyDetails).pipe(
                finalize(() => this.isLoading = false)
            ).subscribe((res) => {
                this.propertyDetails.id = res.body?.id;
                this.alertService.success('ontology-manager.tab.properties.modal.property.created.success');
                this.notifyChanges();
            });
        }
    }

    notifyChanges(): void {
        this.eventManager.broadcast({ name: 'propertyPaneViewChanged' });
        this.clear();
    }

    protected showNoEditableFieldAlert() {
        return this.modeView === ModeView.EDIT && this.propertyDetails?.metadata?.editableFields.length < this.NUM_EDITABLE_FIELDS;
    }

    protected isFormValid(f) {
        return f.form.valid && !this.isLoading && this.propertyDetails.name;
    }

    protected onClassesChanged($event: any) {
        const selectedClassNames = $event.map(propertyClass => propertyClass.text);
        this.propertyDetails.classes = selectedClassNames;
    }

    protected delete() {
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        const message = 'ontology-manager.property.confirm.deletion';
        confirmModalRef.componentInstance.message = this.translateService.instant(message);
        confirmModalRef.result.then(() => {
            this.isLoading = true;
            this.propertyService.delete(this.propertyDetails.id).pipe(
                finalize(() => this.isLoading = false)
            ).subscribe(() => {
                this.alertService.success('ontology-manager.tab.properties.modal.property.deleted.success');
                this.notifyChanges();
            });
        });
    }

    private navigateToVariableView() {
        this.activeModal.dismiss('cancel');
        setTimeout(() => {
            if (this.variableId) {
                this.router.navigate(
                    ['/', { outlets: { popup: ['variables', this.variableId, 'view'] } }],
                    { queryParamsHandling: 'merge' }
                );
            } else if (this.ontologyContext.variableDetails) {
                if (this.propertyDetails.id) {
                    this.ontologyContext.variableDetails.property = structuredClone(this.propertyDetails);
                }
                this.router.navigate(
                    ['/', { outlets: { popup: ['variables', 'new'] } }],
                    { queryParamsHandling: 'merge' }
                );
            }
        });
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscapePressed(event: KeyboardEvent): void {
        event.preventDefault();
        this.navigateToVariableView();

    }
}

@Component({
    selector: 'jhi-property-popup',
    template: ''
})
export class PropertyPopupComponent implements OnInit {

    constructor(private popupService: PopupService,
                private route: ActivatedRoute) {
    }

    ngOnInit(): void {
        const id = this.route.snapshot.params['id'];
        const variableId = this.route.snapshot.params['variableId'];
        const modalRef = this.popupService.open(PropertyDialogComponent as Component, { modalDialogClass: 'modal-lg', backdrop: 'static' });
        modalRef?.then(ngmodalRef => {
            ngmodalRef.componentInstance.propertyId = id ? id : null;
            ngmodalRef.componentInstance.variableId = variableId ? variableId : null;
        });
    }

}
